import { createTheme } from '@mui/material/styles';
import { palette } from './palette';
import { focusVisibleBaseline } from './focus';

/**
 * `webPalette` is a thin, marketing-vocabulary alias over the shared
 * `@munifocus/ui` palette. Adding colors here is not allowed — extend
 * the shared palette instead so Admin / Mobile pick up the same tokens.
 */
export const webPalette = {
  // Brand greens
  green: palette.brand.rail,
  greenDeep: palette.brand.railHover,
  greenAbyss: palette.brand.railAbyss,

  // Brand teals
  teal: palette.brand.accentLight,
  tealHover: palette.brand.accentLightHover,
  tealMute: palette.brand.accentMute,
  tealInk: palette.brand.accent,

  // Semantic
  danger: palette.semantic.emergency,
  /** Error text on the dark bands (subscribe forms): the emergency tint reads as
   *  an error there, where `danger` itself is too dark to read. */
  dangerOnDark: palette.semantic.emergencyTint,

  // Text hierarchy
  ink: palette.text.primary,
  text: palette.text.secondary,
  /** alias of `text` so existing body copy stays readable */
  muted: palette.text.secondary,
  mutedSoft: palette.text.muted,
  mutedOnDark: palette.text.mutedOnDark,

  // Text on dark surfaces
  onDark: palette.text.onDark,
  onDarkStrong: palette.text.onDarkStrong,
  onDarkSoft: palette.text.onDarkSoft,
  onDarkMuted: palette.text.onDarkMuted,
  onDarkSubtle: palette.text.onDarkSubtle,
  onDarkFaint: palette.text.onDarkFaint,
  onDarkDim: palette.text.onDarkDim,
  onDarkGhost: palette.text.onDarkGhost,

  // Surfaces
  white: palette.background.paper,
  cream: palette.background.default,
  parch: palette.background.parch,
  surfaceOnDarkFaint: palette.background.surfaceOnDarkFaint,
  surfaceOnDarkSoft: palette.background.surfaceOnDarkSoft,
  surfaceOnDarkMuted: palette.background.surfaceOnDarkMuted,

  // Lines / borders
  line: palette.border.line,
  borderOnLight: palette.border.onLight,
  borderOnDarkFaint: palette.border.onDarkFaint,
  borderOnDarkSubtle: palette.border.onDarkSubtle,
  borderOnDarkSoft: palette.border.onDarkSoft,
  borderOnDarkMuted: palette.border.onDarkMuted,
  borderOnDarkStrong: palette.border.onDarkStrong,
  borderOnDarkInput: palette.border.onDarkInput,
  borderOnDarkButton: palette.border.onDarkButton,
  borderOnDarkHover: palette.border.onDarkHover,

  // Overlays
  overlayDeepTealSticky: palette.overlay.railSticky,
  overlayBackdrop: palette.overlay.backdrop,
  overlayGridLineOnDark: palette.overlay.gridLineOnDark,
  overlayGridStripeOnDark: palette.overlay.gridStripeOnDark,
  overlayCardShadow: palette.overlay.cardShadow,
  overlayBlackShadow: palette.overlay.blackShadow,
  overlayTealFocusRing: palette.overlay.accentFocusRing,
} as const;

export const webFonts = {
  display: "'Manrope', system-ui, -apple-system, Segoe UI, sans-serif",
  body: "'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, sans-serif",
  bulletin: "'Playfair Display', Georgia, serif",
  /** Monospace stack for codes / identifiers (audit IDs, slugs, mock UI) */
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  /** @deprecated use display */
  serif: "'Manrope', system-ui, -apple-system, Segoe UI, sans-serif",
  /** @deprecated use body */
  sans: "'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, sans-serif",
} as const;

export const webTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: webPalette.green,
      dark: webPalette.greenDeep,
      contrastText: webPalette.onDark,
    },
    secondary: {
      main: webPalette.tealInk,
      dark: webPalette.tealInk,
      light: webPalette.teal,
      contrastText: webPalette.onDark,
    },
    background: { default: webPalette.white, paper: webPalette.white },
    text: { primary: webPalette.ink, secondary: webPalette.text },
    divider: webPalette.line,
  },
  shape: { borderRadius: 7 },
  typography: {
    fontFamily: webFonts.body,
    h1: {
      fontFamily: webFonts.display,
      fontWeight: 700,
      fontSize: '72px',
      lineHeight: 1.05,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: webFonts.display,
      fontWeight: 700,
      fontSize: '42px',
      lineHeight: 1.1,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontFamily: webFonts.display,
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: 1.2,
    },
    h4: { fontFamily: webFonts.display, fontWeight: 600, fontSize: '20px', lineHeight: 1.25 },
    h5: { fontFamily: webFonts.display, fontWeight: 600, fontSize: '18px', lineHeight: 1.3 },
    h6: { fontFamily: webFonts.display, fontWeight: 600, fontSize: '16px', lineHeight: 1.4 },
    body1: { fontFamily: webFonts.body, fontSize: '16px', lineHeight: 1.55 },
    body2: { fontFamily: webFonts.body, fontSize: '14.5px', lineHeight: 1.5 },
    button: {
      fontFamily: webFonts.display,
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 0,
    },
    overline: {
      fontFamily: webFonts.body,
      fontWeight: 600,
      fontSize: '12px',
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
    caption: { fontFamily: webFonts.body, fontSize: '13px' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...focusVisibleBaseline,
        html: {
          backgroundColor: webPalette.green,
          overscrollBehavior: 'none',
        },
        body: {
          backgroundColor: webPalette.white,
          color: webPalette.ink,
          WebkitFontSmoothing: 'antialiased',
          overscrollBehavior: 'none',
          overflowX: 'hidden',
        },
        '#root': { backgroundColor: webPalette.white },
        a: { color: 'inherit', textDecoration: 'none' },
        em: { fontStyle: 'italic' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: {
          borderRadius: 7,
          padding: '14px 26px',
          fontSize: 15,
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.2s ease',
          border: '1px solid transparent',
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: webPalette.green,
            color: webPalette.onDark,
            '&:hover': { backgroundColor: webPalette.greenDeep },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: webPalette.teal,
            color: webPalette.greenDeep,
            '&:hover': { backgroundColor: webPalette.tealHover },
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            borderColor: webPalette.line,
            color: webPalette.ink,
            backgroundColor: 'transparent',
            '&:hover': { borderColor: webPalette.green, backgroundColor: webPalette.cream },
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // WCAG 1.4.11 — the field outline is what identifies the control.
          '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.border.interactive },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: webPalette.line } } },
  },
});

export type WebTheme = typeof webTheme;
